'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import sampleSize from 'lodash/sampleSize';
import Skeleton from '@mui/material/Skeleton';
import SearchBox from '@/components/seo/search-area/SearchBox';
import {
  GroupedHotJotTitlesType,
  SearchType,
  SimplifyJotTitleType,
} from '@/types/job-title';
import {
  COMPANY_ROUTER_PATH,
  JOB_TITLE_PAGE_ROUTER_PATH,
} from '@/constants/router-path';
import { SEARCH_TYPE, SEARCH_MODE_TYPE } from '@/constants/common';
import { trackClickEvent } from '@/utils/site-track';

const Loading = () => {
  return (
    <>
      <Skeleton variant="rounded" width={100} height={40} />
      <Skeleton variant="rounded" width={100} height={40} />
      <Skeleton variant="rounded" width={100} height={40} />
      <Skeleton variant="rounded" width={100} height={40} />
      <Skeleton variant="rounded" width={100} height={40} />
      <Skeleton variant="rounded" width={100} height={40} />
    </>
  );
};

const NameSearch = ({
  hotJobTitles,
}: {
  hotJobTitles: GroupedHotJotTitlesType;
}) => {
  const [randomJobTitles, setRandomJobTitles] = React.useState<
    SimplifyJotTitleType[]
  >([]);
  const router = useRouter();

  const handleSearch = (value: SearchType) => {
    if (!value) return;
    const { id, name, search_type } = value;

    if (search_type === SEARCH_TYPE.JOB_TITLE) {
      router.push(
        `${JOB_TITLE_PAGE_ROUTER_PATH}${value.name_type == 'alias' ? (value.alias_id ? `${value.alias_id}/` : `${id}/?jn=${encodeURIComponent(name)}`) : `${id}/`}`,
      );
    }
    if (search_type === SEARCH_TYPE.JOB_TITLE_CITY) {
      const pinyin = value.city?.pinyin;
      pinyin &&
        router.push(
          `/${pinyin}/${value.name_type == 'alias' ? (value.alias_id ? `${value.alias_id}/` : `${id}/?jn=${encodeURIComponent(name)}`) : `${id}/`}`,
        );
    }
    if (search_type === SEARCH_TYPE.COMPANY) {
      router.push(`${COMPANY_ROUTER_PATH}${id}/`);
    }
  };

  React.useEffect(() => {
    if (hotJobTitles && hotJobTitles.length) {
      const jobTitles = hotJobTitles
        .map((item) => sampleSize(item.job_titles, 4))
        .flat();
      setRandomJobTitles(sampleSize(jobTitles, 8));
    }
  }, []);

  return (
    <div className="-mt-7 px-6">
      <SearchBox
        onChange={handleSearch}
        placeholder="搜索职位·公司"
        className="w-full shadow-5xl rounded-lg"
        inputBorderColor="border border-web-blue-500"
        showEndAdornment={false}
        mode={SEARCH_MODE_TYPE.DATA_SEARCH}
      />
      <h2 className="mt-10 mb-5 font-semibold">热门搜索</h2>
      <div className="flex flex-wrap gap-5">
        {!randomJobTitles.length && <Loading />}
        {randomJobTitles.map((item, index) => (
          <Link
            key={item.id + index}
            className="px-3 py-2 rounded-lg border border-web-grey-550 text-sm font-semibold transition-colors duration-250 hover:bg-web-blue-500/4 hover:border-web-blue-600"
            href={`${JOB_TITLE_PAGE_ROUTER_PATH}${item.id}/`}
            data-ele-group="search_job-title_recommend"
            data-ele-id={`${item.id}_${item.name}`}
            onClick={(e) => trackClickEvent(e, true)}
          >
            {item.name}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default NameSearch;
